import { motion } from 'framer-motion'
import type { HTMLMotionProps } from 'framer-motion'
import React from 'react'
import {
	prefersReducedMotion,
	staggerChild,
	staggerContainer,
} from '#app/utils/animations'
import { cn } from '#app/utils/misc'

interface StaggerChildrenProps extends HTMLMotionProps<'div'> {
	children: React.ReactNode
	staggerDelay?: number
	className?: string
}

/**
 * StaggerChildren - Component for staggered child animations
 * Useful for form fields appearing sequentially
 * Respects prefers-reduced-motion
 */
export function StaggerChildren({
	children,
	staggerDelay = 0.05,
	className,
	...props
}: StaggerChildrenProps) {
	const shouldAnimate = !prefersReducedMotion()

	const customStaggerContainer = {
		...staggerContainer,
		visible: {
			...staggerContainer.visible,
			transition: {
				...staggerContainer.visible.transition,
				staggerChildren: staggerDelay,
			},
		},
	}

	return (
		<motion.div
			initial={shouldAnimate ? 'hidden' : false}
			animate={shouldAnimate ? 'visible' : false}
			variants={customStaggerContainer}
			className={cn(className)}
			{...props}
		>
			{React.Children.map(children, (child, index) => {
				if (!React.isValidElement(child)) return child

				return (
					<motion.div
						key={index}
						variants={shouldAnimate ? staggerChild : undefined}
					>
						{child}
					</motion.div>
				)
			})}
		</motion.div>
	)
}
